import { FormControlLabel, Switch, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useAtom } from "jotai";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { publishClass } from "@/services/class";
import { configs } from "@/configs";
import { selectedBranchAtom } from "@/atom/global";
import { refetchQueries } from "@/utils";

import { getPublished } from "../utils";

import type { Class } from "@/models";
import type { AxiosErrorWithData } from "@/client/api";

type ClassStatusSwitchProps = {
  data: Class;
  fetchKeys?: string[];
};

const statusText = ["ปิดให้บริการ", "เปิดให้บริการ"];

export function ClassStatusSwitch({
  data,
  fetchKeys = [],
}: ClassStatusSwitchProps) {
  const [selectedBranch] = useAtom(selectedBranchAtom);
  const { id, branches } = data;
  const isActive = getPublished(branches, selectedBranch);
  const [isOpen, setIsOpen] = useState(false);
  const activeStatus = statusText[+isActive];
  const changeStatusText = statusText[+!isActive];
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const onClose = () => setIsOpen(false);

  const { mutate: publish, isLoading } = useMutation(publishClass, {
    onSuccess: async () => {
      enqueueSnackbar("อัพเดทสถานะสำเร็จ", { variant: "success" });
      await refetchQueries({ queryClient, fetchKeys });
      onClose();
    },
    onError: (error: AxiosErrorWithData) => {
      console.error(error);
      enqueueSnackbar(
        error.response?.data.message ?? configs.unknownErrorMessage,
        { variant: "error" }
      );
    },
  });

  const onChangeClassStatus = () => setIsOpen(true);

  const confirmDialog = {
    maxWidth: "xs" as const,
    open: isOpen,
    onClose,
    onConfirm: () => publish({ id: id.toString(), isPublished: !isActive }),
    title: `คุณต้องการ${changeStatusText}หรือไม่`,
    confirmMessage: "ใช่",
    cancelMessage: "ไม่ใช่",
    loading: isLoading,
  };

  return (
    <FormControlLabel
      control={
        <>
          <Switch
            value={id}
            checked={isActive}
            onChange={onChangeClassStatus}
            disabled={isOpen}
          />
          <ConfirmDialog {...confirmDialog} />
        </>
      }
      label={<Typography variant="body2">{activeStatus}</Typography>}
      onClick={(event) => {
        event.stopPropagation();
      }}
    />
  );
}
